import { HaulierUnsupportedCountryModal } from './haulier-unsupported-country-modal'
import { countriesService } from '../../business/service/countries-service'
import { joiResolver } from '@hookform/resolvers/joi'
import {
  TenantHaulierRegistrationData,
  TenantHaulierRegistrationSchema,
} from '@zeus/common/lib/business/model/auth/haulier-registration-data'
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Button, Col, Form, OverlayTrigger, Popover, Row } from 'react-bootstrap'
import { useForm, useWatch } from 'react-hook-form'
import { tooltipIcon } from 'src/component/util/image'

export const HaulierTenantDetailsForm = forwardRef(
  (
    props: {
      defaultValues: TenantHaulierRegistrationData | null
      onSubmit: (data: TenantHaulierRegistrationData) => void
    },
    ref,
  ): React.ReactElement => {
    const [showModal, setShowModal] = useState(false)
    const [allCountries, setAllCountries] = useState<string[]>([])
    const [supportedCountries, setSupportedCountries] = useState<string[]>([])
    const {
      formState: { errors, isValid, isSubmitted },
      register,
      control,
      watch,
      setValue,
      handleSubmit,
    } = useForm<TenantHaulierRegistrationData>({
      mode: 'all',
      criteriaMode: 'all',
      defaultValues: props.defaultValues || {},
      // TODO: Remove casting to any
      resolver: joiResolver(TenantHaulierRegistrationSchema as any),
    })

    const country = useWatch({ control, name: 'countryOfRegistration' })

    useImperativeHandle(
      ref,
      () => {
        return {
          submit(): void {
            handleSubmit((data) => isValid && props.onSubmit(data))()
          },
        }
      },
      [],
    )

    useEffect(() => {
      const setupCountriesArrays = async (): Promise<void> => {
        const allCountriesData = await countriesService.fetchAllCountriesCommonNames()
        setAllCountries(allCountriesData)
        const supportedCountriesData = await countriesService.fetchSupportedCountriesCommonNames()
        setSupportedCountries(supportedCountriesData)
      }

      setupCountriesArrays()
    }, [])
    // TODO temporary remove popup for unsupported countries
    // useEffect(() => {
    //   if (!country || supportedCountries.includes(country)) return
    //   setShowModal(true)
    //   setValue('countryOfRegistration', '')
    // }, [country])

    return (
      <>
        <Form>
          <Row className="gx-5">
            <Col sm={12} md={6}>
              <Form.Group className="form-group" controlId="formBasicCompanyName">
                <Form.Label>Company legal name</Form.Label>
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement="top"
                  overlay={
                    <Popover id="popover-basic">
                      <Popover.Body>
                        Your company name must be the same entity that is listed on your Goods in Transit insurance and Operator's
                        Licence copies (uploaded later)
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button variant="link">
                    <img className="tooltip-icon" src={tooltipIcon} />
                  </Button>
                </OverlayTrigger>
                <Form.Control
                  type="text"
                  placeholder="Company Name"
                  {...register('companyName', { required: true })}
                  isInvalid={isSubmitted && errors.companyName !== undefined}
                />
                {isSubmitted && <Form.Control.Feedback type="invalid">{errors.companyName?.message}</Form.Control.Feedback>}
              </Form.Group>
              <Form.Group
                className={`form-group ${isSubmitted && errors.countryOfRegistration?.message ? 'select-error' : ''}`}
                controlId="formBasicCountryOfRegistration"
              >
                <Form.Label>Country of Registration</Form.Label>
                <Form.Select
                  value={watch('countryOfRegistration') || props.defaultValues?.countryOfRegistration || ''}
                  onChange={(e) => setValue('countryOfRegistration', e.target.value)}
                >
                  <option value="">Select a country</option>
                  {allCountries.map((country) => (
                    <option key={country} value={country}>
                      {country}
                    </option>
                  ))}
                </Form.Select>
                {isSubmitted && (
                  <Form.Control.Feedback type="invalid">{errors.countryOfRegistration?.message}</Form.Control.Feedback>
                )}
              </Form.Group>

              <Form.Group className="form-group" controlId="formBasicTaxNumber">
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  placement="top"
                  overlay={
                    <Popover id="popover-basic">
                      <Popover.Body>
                        VAT, BTW, etc. If you do not have a registered tax number, please state: “Not applicable”
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button variant="link">
                    <img className="tooltip-icon" src={tooltipIcon} />
                  </Button>
                </OverlayTrigger>
                <Form.Label>Registered tax Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Registered tax Number"
                  {...register('taxNumber', { required: true })}
                  isInvalid={isSubmitted && errors.taxNumber !== undefined}
                />
                {isSubmitted && <Form.Control.Feedback type="invalid">{errors.taxNumber?.message}</Form.Control.Feedback>}
              </Form.Group>

              <Form.Group className="form-group" controlId="formBasicRegistrationNumber">
                <Form.Label>Company registration number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Company registration number"
                  {...register('registrationNumber', { required: true })}
                  isInvalid={isSubmitted && errors.registrationNumber !== undefined}
                />
                {isSubmitted && (
                  <Form.Control.Feedback type="invalid">{errors.registrationNumber?.message}</Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
            <Col sm={12} md={6}>
              <Form.Group className="form-group" controlId="formBasicAddress">
                <Form.Label>Company address</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Company address"
                  {...register('address', { required: true })}
                  isInvalid={isSubmitted && errors.address !== undefined}
                />
                {isSubmitted && <Form.Control.Feedback type="invalid">{errors.address?.message}</Form.Control.Feedback>}
              </Form.Group>
              <Row>
                <Col sm={12} md={6}>
                  <Form.Group className="form-group" controlId="formBasicCoty">
                    <Form.Label>Town / City</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="City"
                      {...register('city', { required: true })}
                      isInvalid={isSubmitted && errors.city !== undefined}
                    />
                    {isSubmitted && <Form.Control.Feedback type="invalid">{errors.city?.message}</Form.Control.Feedback>}
                  </Form.Group>
                  <Form.Group className="form-group" controlId="formBasicPostcode">
                    <Form.Label>Postcode</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Postcode"
                      {...register('postcode', { required: true })}
                      isInvalid={isSubmitted && errors.postcode !== undefined}
                    />
                    {isSubmitted && <Form.Control.Feedback type="invalid">{errors.postcode?.message}</Form.Control.Feedback>}
                  </Form.Group>
                </Col>
                <Col sm={12} md={6}>
                  <Form.Group className="form-group" controlId="formBasicState">
                    <Form.Label>State</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="State"
                      {...register('state', { required: true })}
                      isInvalid={isSubmitted && errors.state !== undefined}
                    />
                    {isSubmitted && <Form.Control.Feedback type="invalid">{errors.state?.message}</Form.Control.Feedback>}
                  </Form.Group>
                  <Form.Group className="form-group" controlId="formBasicCountry">
                    <Form.Label>Country</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Country"
                      {...register('country', { required: true })}
                      isInvalid={isSubmitted && errors.country !== undefined}
                    />
                    {isSubmitted && <Form.Control.Feedback type="invalid">{errors.country?.message}</Form.Control.Feedback>}
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="form-group" controlId="formBasicManager">
                <Form.Label>Manager responsible for health and safety issues</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Manager name"
                  {...register('personResponsible', { required: true })}
                  isInvalid={isSubmitted && errors.personResponsible !== undefined}
                />
                {isSubmitted && <Form.Control.Feedback type="invalid">{errors.personResponsible?.message}</Form.Control.Feedback>}
              </Form.Group>
            </Col>
          </Row>
        </Form>
        <HaulierUnsupportedCountryModal showModal={showModal} setShowModal={setShowModal} />
      </>
    )
  },
)
